"use client";
import CN from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { faSync } from "@fortawesome/pro-solid-svg-icons/faSync";
import { keyframes, styled } from "@mui/material/styles";
import { forwardRef } from "react";
import { BoxProps } from "@mui/material/Box";
import Box from "@mui/material/Box";

export interface LoadingIndicatorProps {
  // Title to render if `status` is null/undefined.
  title?: React.ReactNode;
  // Content to render if `status` is null/undefined.
  content?: React.ReactNode;

  /**
   * If a number is provided, a progress bar will render that will move from 0%
   * to 100% in that amount of time (in milliseconds), so long as the component
   * is in loading state.
   */
  expectedTimeMs?: number;

  // Render a successful (green) or unsuccessful (red) message immediately.
  status?: boolean | null;
  // Content to render if `status` is True.
  success?: React.ReactNode;
  // Content to render if `status` is False.
  failure?: React.ReactNode;

  isSubtle?: boolean;

  // If True, external negative margin will not be applied. The LoadingIndicator still expects
  // to be in a <ExternalCopyBox>.
  hasContentAbove?: boolean;

  noNegativeMargin?: boolean;

  // Should fill the full screen with absolute positioning
  fullScreen?: boolean;

  boxProps?: BoxProps;
}

/**
 * Renders a loading "block" -- useful when the use case of the page is being fetched, calculated,
 * or is otherwise delayed. The design is intentionally aggressive (although use `isSubtle` to tone
 * back the colors) and dominant; when you use `<LoadingIndicator>`, the fact that things are loading
 * should be the main focus.
 *
 * This component supports the most common behavior, which is that something is loading and then
 * either a success or failure result is delivered. To do so, it allows passing multiple content blocks
 * to be rendered for each of loading, successful, and failure states. Which of `content`, `success`,
 * and `failure` is rendered is controlled by the `status`.
 *
 * # Component "lifecycle"
 *
 * - The content is loading. Pass `status = null`. If you have an estimate for how long loading will take,
 *   pass `expectedTimeMs` to display a progress bar.
 * - The content is done loading and the result is successful. Pass `status = true` to render `success`
 *   on a green background.
 * - The content is done loading and the result is unsuccessful. Pass `status = false` to render `failure`
 *   on a red background.
 */
export const LoadingIndicator = forwardRef<HTMLDivElement, LoadingIndicatorProps>(
  (
    {
      title = "Just a second",
      content,
      expectedTimeMs,
      status,
      success,
      failure,
      isSubtle = false,
      hasContentAbove = false,
      noNegativeMargin = false,
      fullScreen = false,
      boxProps,
    }: LoadingIndicatorProps,
    ref: React.Ref<HTMLDivElement>
  ) => (
    <LIWrapper
      ref={ref}
      className={CN({
        "li-wrapper": true,
        successLight: status === true,
        failure: status === false,
        is_subtle: isSubtle,
        "has-content-above": hasContentAbove,
        "no-negative-margin": noNegativeMargin,
        "full-screen": fullScreen,
      })}
      {...boxProps}
      data-testid="loading-indicator"
    >
      {status === true ? (
        <>
          <LIIcon icon={faCheck} />
          {success}
        </>
      ) : status === false ? (
        <>
          <LIIcon icon={faTimes} />
          {failure}
        </>
      ) : (
        <>
          <LIIcon icon={faSync} spin />
          <h1>{title}</h1>
          {content}
          {!!expectedTimeMs && (
            <LIProgressBarWrapper>
              <LIProgressBar $time={expectedTimeMs} />
            </LIProgressBarWrapper>
          )}
        </>
      )}
    </LIWrapper>
  )
);

const LIWrapper = styled(Box)`
  padding: var(--horizontal-padding);
  min-height: max(200px, 40vh);
  flex: 1;
  width: 100%;

  border-radius: 8px;
  background: ${({ theme }) => theme.palette.primary.dark}
    linear-gradient(to top, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0));
  text-align: center;
  color: ${({ theme }) => theme.palette.dream.grayLight};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 250ms linear all;

  &.no-negative-margin {
    margin: 0;
  }

  &.has-content-above {
    margin-top: 2rem;
  }

  @media (max-width: 890px) {
    border-radius: 0;
  }

  &.success {
    background-color: ${({ theme }) => theme.palette.success.dark};
  }

  &.failure {
    background-color: ${({ theme }) => theme.palette.error.dark};
  }

  &.is_subtle {
    background: ${({ theme }) => theme.palette.dream.grayLight};
    color: ${({ theme }) => theme.palette.primary.dark};
  }

  &.full-screen {
    position: fixed;
    margin: 0;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 9999;
    border-radius: 0;

    // This helps correct the visual weight of the text by shifting everything up
    padding-bottom: calc(2 * var(--horizontal-padding));
  }

  & h1,
  & p {
    color: inherit;
    max-width: 500px;
  }
`;

const LIIcon = styled(FontAwesomeIcon)`
  font-size: 2rem;

  &:not(:only-child) {
    margin-bottom: 1rem;
  }

  .li-wrapper.full-screen & {
    font-size: 3rem;
  }

  .li-wrapper.success &,
  .li-wrapper.failure & {
    font-size: 3rem;
  }
`;

const liProgressBarAnimate = keyframes`
    from {
        width: 0
    }
    to {
        width: 100%
    }
`;

const LIProgressBarWrapper = styled("div")`
  width: 100%;
  max-width: 500px;
  margin-top: 1rem;
  border-radius: 8px;

  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 2px;
`;

const LIProgressBar = styled("div", {
  shouldForwardProp: (prop) => prop !== "$time",
})<{ $time: number }>`
  height: 5px;
  width: 0.4rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  animation: ${liProgressBarAnimate} ${({ $time }) => $time}ms linear forwards;
`;
